// Built-in SVG patterns for vase texture displacement.
// Each pattern is rendered to a small bitmap and converted to a luminance grid that the
// vase mesh builder samples to displace its surface radially.

import type { TextureGrid } from '../core/vaseModel';
import { luminanceToSdf } from './sdf';

export interface PatternDef {
  id: string;
  name: string;
  /** Inline SVG body (without outer <svg>). Black = bumps inward, white = bumps outward. */
  svg: string;
}

const TILE = 64;

function wrap(body: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${TILE} ${TILE}" width="${TILE}" height="${TILE}">
    <rect width="${TILE}" height="${TILE}" fill="#000000"/>
    ${body}
  </svg>`;
}

/** Dots */
const dots = wrap(`
  <circle cx="16" cy="16" r="9" fill="white"/>
  <circle cx="48" cy="16" r="9" fill="white"/>
  <circle cx="16" cy="48" r="9" fill="white"/>
  <circle cx="48" cy="48" r="9" fill="white"/>
  <circle cx="32" cy="32" r="9" fill="white"/>
`);

/** Horizontal lines */
const lines = wrap(`
  <rect x="0" y="6" width="64" height="6" fill="white"/>
  <rect x="0" y="22" width="64" height="6" fill="white"/>
  <rect x="0" y="38" width="64" height="6" fill="white"/>
  <rect x="0" y="54" width="64" height="6" fill="white"/>
`);

/** Diagonal stripes */
const diagonal = wrap(`
  <g stroke="white" stroke-width="6" fill="none">
    <line x1="-8" y1="-8" x2="72" y2="72"/>
    <line x1="-8" y1="20" x2="72" y2="100"/>
    <line x1="-8" y1="-36" x2="72" y2="44"/>
  </g>
`);

/** Hexagon honeycomb */
const hexagons = wrap(`
  <g fill="none" stroke="white" stroke-width="3">
    <polygon points="32,4 56,18 56,46 32,60 8,46 8,18"/>
    <polygon points="0,4 8,8 8,18 0,22"/>
    <polygon points="64,4 56,8 56,18 64,22"/>
    <polygon points="0,42 8,46 8,56 0,60"/>
    <polygon points="64,42 56,46 56,56 64,60"/>
  </g>
`);

/** Sine waves */
const waves = wrap(`
  <g fill="none" stroke="white" stroke-width="3">
    <path d="M 0 16 Q 16 4 32 16 T 64 16"/>
    <path d="M 0 32 Q 16 20 32 32 T 64 32"/>
    <path d="M 0 48 Q 16 36 32 48 T 64 48"/>
  </g>
`);

/** Zigzag */
const zigzag = wrap(`
  <g fill="none" stroke="white" stroke-width="3">
    <polyline points="0,12 16,28 32,12 48,28 64,12"/>
    <polyline points="0,36 16,52 32,36 48,52 64,36"/>
  </g>
`);

/** Brick pattern */
const brick = wrap(`
  <g fill="none" stroke="white" stroke-width="2">
    <rect x="0" y="0" width="32" height="16"/>
    <rect x="32" y="0" width="32" height="16"/>
    <rect x="-16" y="16" width="32" height="16"/>
    <rect x="16" y="16" width="32" height="16"/>
    <rect x="48" y="16" width="32" height="16"/>
    <rect x="0" y="32" width="32" height="16"/>
    <rect x="32" y="32" width="32" height="16"/>
    <rect x="-16" y="48" width="32" height="16"/>
    <rect x="16" y="48" width="32" height="16"/>
    <rect x="48" y="48" width="32" height="16"/>
  </g>
`);

/** Fish scales */
const scales = wrap(`
  <g fill="none" stroke="white" stroke-width="2">
    <path d="M 0 16 A 16 16 0 0 1 32 16"/>
    <path d="M 32 16 A 16 16 0 0 1 64 16"/>
    <path d="M -16 32 A 16 16 0 0 1 16 32"/>
    <path d="M 16 32 A 16 16 0 0 1 48 32"/>
    <path d="M 48 32 A 16 16 0 0 1 80 32"/>
    <path d="M 0 48 A 16 16 0 0 1 32 48"/>
    <path d="M 32 48 A 16 16 0 0 1 64 48"/>
  </g>
`);

/** Herringbone */
const herringbone = wrap(`
  <g fill="white">
    <rect x="0" y="0" width="20" height="6" transform="rotate(45 10 3)"/>
    <rect x="20" y="0" width="20" height="6" transform="rotate(-45 30 3)"/>
    <rect x="40" y="0" width="20" height="6" transform="rotate(45 50 3)"/>
    <rect x="0" y="20" width="20" height="6" transform="rotate(45 10 23)"/>
    <rect x="20" y="20" width="20" height="6" transform="rotate(-45 30 23)"/>
    <rect x="40" y="20" width="20" height="6" transform="rotate(45 50 23)"/>
    <rect x="0" y="40" width="20" height="6" transform="rotate(45 10 43)"/>
    <rect x="20" y="40" width="20" height="6" transform="rotate(-45 30 43)"/>
    <rect x="40" y="40" width="20" height="6" transform="rotate(45 50 43)"/>
  </g>
`);

/** Crosshatch */
const crosshatch = wrap(`
  <g stroke="white" stroke-width="3" fill="none">
    <line x1="0" y1="0" x2="64" y2="64"/>
    <line x1="0" y1="64" x2="64" y2="0"/>
    <line x1="-32" y1="32" x2="32" y2="96"/>
    <line x1="32" y1="-32" x2="96" y2="32"/>
    <line x1="-32" y1="-32" x2="32" y2="32"/>
    <line x1="32" y1="32" x2="96" y2="96"/>
  </g>
`);

/** Stars */
const stars = wrap(`
  <g fill="white">
    <polygon points="32,8 36,24 52,24 39,33 44,49 32,40 20,49 25,33 12,24 28,24"/>
    <polygon points="8,52 10,58 16,58 11,62 13,68 8,64 3,68 5,62 0,58 6,58"/>
    <polygon points="56,52 58,58 64,58 59,62 61,68 56,64 51,68 53,62 48,58 54,58"/>
    <polygon points="8,4 10,10 16,10 11,14 13,20 8,16 3,20 5,14 0,10 6,10"/>
    <polygon points="56,4 58,10 64,10 59,14 61,20 56,16 51,20 53,14 48,10 54,10"/>
  </g>
`);

/** Concentric ring grid */
const rings = wrap(`
  <g fill="none" stroke="white" stroke-width="2">
    <circle cx="16" cy="16" r="6"/>
    <circle cx="16" cy="16" r="12"/>
    <circle cx="48" cy="16" r="6"/>
    <circle cx="48" cy="16" r="12"/>
    <circle cx="32" cy="32" r="6"/>
    <circle cx="32" cy="32" r="12"/>
    <circle cx="16" cy="48" r="6"/>
    <circle cx="16" cy="48" r="12"/>
    <circle cx="48" cy="48" r="6"/>
    <circle cx="48" cy="48" r="12"/>
  </g>
`);

/** Grid mesh */
const grid = wrap(`
  <g stroke="white" stroke-width="2" fill="none">
    <line x1="16" y1="0" x2="16" y2="64"/>
    <line x1="32" y1="0" x2="32" y2="64"/>
    <line x1="48" y1="0" x2="48" y2="64"/>
    <line x1="0" y1="16" x2="64" y2="16"/>
    <line x1="0" y1="32" x2="64" y2="32"/>
    <line x1="0" y1="48" x2="64" y2="48"/>
  </g>
`);

/** Triangles (alternating up/down) */
const triangles = wrap(`
  <g fill="white">
    <polygon points="0,52 16,20 32,52"/>
    <polygon points="32,52 48,20 64,52"/>
    <polygon points="16,12 32,44 0,44"/>
    <polygon points="48,12 64,44 32,44"/>
  </g>
`);

/** Diamonds (rhombi) */
const diamonds = wrap(`
  <g fill="white">
    <polygon points="16,4 28,16 16,28 4,16"/>
    <polygon points="48,4 60,16 48,28 36,16"/>
    <polygon points="32,20 44,32 32,44 20,32"/>
    <polygon points="16,36 28,48 16,60 4,48"/>
    <polygon points="48,36 60,48 48,60 36,48"/>
  </g>
`);

/** Plus / cross signs */
const plus = wrap(`
  <g fill="white">
    <rect x="14" y="6" width="4" height="20"/>
    <rect x="6" y="14" width="20" height="4"/>
    <rect x="46" y="6" width="4" height="20"/>
    <rect x="38" y="14" width="20" height="4"/>
    <rect x="30" y="38" width="4" height="20"/>
    <rect x="22" y="46" width="20" height="4"/>
  </g>
`);

/** Chevron (V shapes stacked) */
const chevron = wrap(`
  <g fill="none" stroke="white" stroke-width="3">
    <polyline points="0,12 16,4 32,12 48,4 64,12"/>
    <polyline points="0,28 16,20 32,28 48,20 64,28"/>
    <polyline points="0,44 16,36 32,44 48,36 64,44"/>
    <polyline points="0,60 16,52 32,60 48,52 64,60"/>
  </g>
`);

/** Stylized leaves */
const leaves = wrap(`
  <g fill="white">
    <ellipse cx="16" cy="16" rx="6" ry="12" transform="rotate(30 16 16)"/>
    <ellipse cx="48" cy="16" rx="6" ry="12" transform="rotate(-30 48 16)"/>
    <ellipse cx="32" cy="32" rx="6" ry="12" transform="rotate(60 32 32)"/>
    <ellipse cx="16" cy="48" rx="6" ry="12" transform="rotate(-30 16 48)"/>
    <ellipse cx="48" cy="48" rx="6" ry="12" transform="rotate(30 48 48)"/>
  </g>
`);

/** Flowers (5-petal blossom motif) */
const flowers = wrap(`
  <g fill="white">
    <g transform="translate(16 16)">
      <circle cx="0" cy="-6" r="4"/><circle cx="6" cy="-2" r="4"/>
      <circle cx="4" cy="6" r="4"/><circle cx="-4" cy="6" r="4"/>
      <circle cx="-6" cy="-2" r="4"/><circle cx="0" cy="0" r="2.5" fill="#444"/>
    </g>
    <g transform="translate(48 48)">
      <circle cx="0" cy="-6" r="4"/><circle cx="6" cy="-2" r="4"/>
      <circle cx="4" cy="6" r="4"/><circle cx="-4" cy="6" r="4"/>
      <circle cx="-6" cy="-2" r="4"/><circle cx="0" cy="0" r="2.5" fill="#444"/>
    </g>
  </g>
`);

/** Snowflake-like radial */
const snowflake = wrap(`
  <g stroke="white" stroke-width="2" fill="none" transform="translate(32 32)">
    <line x1="0" y1="-24" x2="0" y2="24"/>
    <line x1="-24" y1="0" x2="24" y2="0"/>
    <line x1="-17" y1="-17" x2="17" y2="17"/>
    <line x1="-17" y1="17" x2="17" y2="-17"/>
    <circle cx="0" cy="0" r="6"/>
    <circle cx="0" cy="-18" r="3"/><circle cx="0" cy="18" r="3"/>
    <circle cx="-18" cy="0" r="3"/><circle cx="18" cy="0" r="3"/>
  </g>
`);

/** Sun-burst rays */
const sunburst = wrap(`
  <g stroke="white" stroke-width="2" fill="none" transform="translate(32 32)">
    <circle cx="0" cy="0" r="6" fill="white"/>
    <line x1="0" y1="-26" x2="0" y2="-12"/>
    <line x1="0" y1="26" x2="0" y2="12"/>
    <line x1="-26" y1="0" x2="-12" y2="0"/>
    <line x1="26" y1="0" x2="12" y2="0"/>
    <line x1="-18" y1="-18" x2="-9" y2="-9"/>
    <line x1="18" y1="18" x2="9" y2="9"/>
    <line x1="-18" y1="18" x2="-9" y2="9"/>
    <line x1="18" y1="-18" x2="9" y2="-9"/>
  </g>
`);

/** Woven basket */
const basket = wrap(`
  <g fill="white">
    <rect x="0" y="6" width="14" height="6"/>
    <rect x="20" y="6" width="14" height="6"/>
    <rect x="40" y="6" width="14" height="6"/>
    <rect x="10" y="22" width="14" height="6"/>
    <rect x="30" y="22" width="14" height="6"/>
    <rect x="50" y="22" width="14" height="6"/>
    <rect x="0" y="38" width="14" height="6"/>
    <rect x="20" y="38" width="14" height="6"/>
    <rect x="40" y="38" width="14" height="6"/>
    <rect x="10" y="54" width="14" height="6"/>
    <rect x="30" y="54" width="14" height="6"/>
    <rect x="50" y="54" width="14" height="6"/>
  </g>
`);

/** Lattice / diagonal grid */
const lattice = wrap(`
  <g stroke="white" stroke-width="2" fill="none">
    <line x1="0" y1="16" x2="64" y2="16"/>
    <line x1="0" y1="48" x2="64" y2="48"/>
    <line x1="-16" y1="0" x2="32" y2="48"/>
    <line x1="16" y1="0" x2="64" y2="48"/>
    <line x1="32" y1="0" x2="80" y2="48"/>
    <line x1="-16" y1="48" x2="32" y2="0"/>
    <line x1="16" y1="48" x2="64" y2="0"/>
    <line x1="32" y1="48" x2="80" y2="0"/>
  </g>
`);

/** Greek key / meander */
const meander = wrap(`
  <g stroke="white" stroke-width="2" fill="none">
    <polyline points="4,4 28,4 28,20 12,20 12,12 20,12 20,28 4,28 4,4"/>
    <polyline points="36,36 60,36 60,52 44,52 44,44 52,44 52,60 36,60 36,36"/>
  </g>
`);

export const BUILTIN_PATTERNS: PatternDef[] = [
  { id: 'dots', name: 'Noktalar', svg: dots },
  { id: 'lines', name: 'Çizgiler', svg: lines },
  { id: 'diagonal', name: 'Çapraz', svg: diagonal },
  { id: 'hexagons', name: 'Petek', svg: hexagons },
  { id: 'waves', name: 'Dalgalar', svg: waves },
  { id: 'zigzag', name: 'Zikzak', svg: zigzag },
  { id: 'brick', name: 'Tuğla', svg: brick },
  { id: 'scales', name: 'Pul', svg: scales },
  { id: 'herringbone', name: 'Balık Sırtı', svg: herringbone },
  { id: 'crosshatch', name: 'Çapraz Tarama', svg: crosshatch },
  { id: 'stars', name: 'Yıldızlar', svg: stars },
  { id: 'rings', name: 'Halkalar', svg: rings },
  { id: 'grid', name: 'Izgara', svg: grid },
  { id: 'triangles', name: 'Üçgenler', svg: triangles },
  { id: 'diamonds', name: 'Eşkenar', svg: diamonds },
  { id: 'plus', name: 'Artılar', svg: plus },
  { id: 'chevron', name: 'Şerit V', svg: chevron },
  { id: 'leaves', name: 'Yapraklar', svg: leaves },
  { id: 'flowers', name: 'Çiçekler', svg: flowers },
  { id: 'snowflake', name: 'Kar Tanesi', svg: snowflake },
  { id: 'sunburst', name: 'Güneş', svg: sunburst },
  { id: 'basket', name: 'Hasır', svg: basket },
  { id: 'lattice', name: 'Kafes', svg: lattice },
  { id: 'meander', name: 'Meander', svg: meander }
];

/** Render an SVG string into a TextureGrid for vase displacement. */
export async function svgToTextureGrid(
  svg: string,
  intensity: number,
  repeatU: number,
  repeatV: number,
  size = 128
): Promise<TextureGrid> {
  // Some browsers (and some SVGs without explicit width/height) fail to decode
  // when loaded via Blob URL + img.decode(). A base64 data URL + onload event is
  // the most universally compatible path.
  const ensured = ensureSvgDimensions(svg);
  const dataUrl = svgToDataUrl(ensured);
  const img = await loadImage(dataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2d context unavailable');
  // Black background → lum=0 in non-drawn areas; built-in patterns draw white
  // shapes (lum=1). Convention: lum>0.5 marks where the accent material is
  // applied. The shader's step(0.5, lum) maps 0→base, 1→accent.
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, size, size);
  ctx.drawImage(img, 0, 0, size, size);
  const pix = ctx.getImageData(0, 0, size, size);
  const lum = new Float32Array(size * size);
  for (let i = 0; i < size * size; i++) {
    const r = pix.data[i * 4];
    const g = pix.data[i * 4 + 1];
    const b = pix.data[i * 4 + 2];
    lum[i] = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  }
  // Convert raster to vector-quality SDF — boundary is implicit at sdf=0,
  // shader will smoothstep with fwidth for pixel-perfect AA at any zoom.
  const data = luminanceToSdf(lum, size, size);
  return { w: size, h: size, data, intensity, repeatU, repeatV };
}

/** Make sure the SVG has width/height attributes so it rasterizes at the right size. */
function ensureSvgDimensions(svg: string): string {
  // Inject width="64" height="64" right after <svg if missing.
  if (/\<svg[^>]*\swidth=/.test(svg) && /\<svg[^>]*\sheight=/.test(svg)) return svg;
  return svg.replace(/<svg(\b[^>]*)>/, '<svg$1 width="64" height="64">');
}

/** UTF-8-safe base64 encoding. btoa fails on multi-byte chars; this handles them. */
function utf8Btoa(s: string): string {
  return btoa(unescape(encodeURIComponent(s)));
}

function svgToDataUrl(svg: string): string {
  return `data:image/svg+xml;base64,${utf8Btoa(svg)}`;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image decode failed: ' + src.slice(0, 80)));
    img.src = src;
  });
}

/** Build a small data-URL thumbnail for the gallery. */
export function patternThumbnailDataUrl(svg: string): string {
  return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
}
